import { useState, useEffect } from "react";

import {
  collection,
  doc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  increment,
  arrayUnion,
} from "firebase/firestore";
import { db } from "../firebase/config.js";

// ────────────────────────────────────────────────────────────
// LOGGING SAFETY HELPER
// ────────────────────────────────────────────────────────────
// Collection/document identifiers passed into these hooks are
// mostly hardcoded, but a few are threaded through from props/
// route params. Strip newlines/control characters and cap length
// before writing them into any console.* call so log output can't
// be spoofed or split by a crafted identifier.
function sanitizeForLog(value) {
  if (value === undefined || value === null) return "";
  return String(value)
    .replace(/[\r\n\t]/g, " ")
    .replace(/[^\x20-\x7E]/g, "")
    .slice(0, 200);
}

// ────────────────────────────────────────────────────────────
// GENERIC HOOKS
// ────────────────────────────────────────────────────────────

/**
 * Real-time listener for an entire collection with optional
 * Firestore query constraints.
 * NOTE: Pass constraints as a stable reference (e.g. useMemo)
 * if used inside a component, or pass them here as primitives.
 */
export function useCollection(collectionName, constraints = []) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Serialise constraints so we can use them as a stable dep
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const constraintKey = JSON.stringify(constraints.map((c) => c.toString()));

  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, collectionName), ...constraints);
    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error(
          `[useCollection] ${sanitizeForLog(collectionName)}:`,
          err.code,
          err.message,
        );
        setError(err.message);
        setLoading(false);
      },
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionName, constraintKey]);

  return { data, loading, error };
}

/**
 * Real-time listener for a single Firestore document.
 */
export function useDocument(collectionName, docId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Guard inside useEffect — hook itself is always called
    if (!docId) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsub = onSnapshot(
      doc(db, collectionName, docId),
      (snap) => {
        setData(snap.exists() ? { id: snap.id, ...snap.data() } : null);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error(
          `[useDocument] ${sanitizeForLog(collectionName)}/${sanitizeForLog(docId)}:`,
          err.code,
        );
        setError(err.message);
        setLoading(false);
      },
    );
    return unsub;
  }, [collectionName, docId]);

  return { data, loading, error };
}

// ────────────────────────────────────────────────────────────
// ATTENDANCE HOOKS
// ────────────────────────────────────────────────────────────

/** Today's attendance record for a specific user. */
export function useTodayAttendance(userId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Guard inside useEffect — this is the correct pattern
    if (!userId) {
      setData(null);
      setLoading(false);
      return;
    }

    const today = getTodayStr();
    setLoading(true);

    const q = query(
      collection(db, "attendance"),
      where("userId", "==", userId),
      where("date", "==", today)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        if (snap.empty) {
          setData(null);
        } else {
          const records = snap.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          // Sort by checkIn descending client-side
          records.sort((a, b) => {
            const t1 = a.checkIn?.toMillis?.() ?? 0;
            const t2 = b.checkIn?.toMillis?.() ?? 0;
            return t2 - t1;
          });
          const checkedOutRecord = records.find((r) => !!r.checkOut);
          if (checkedOutRecord) {
            setData(checkedOutRecord);
          } else {
            setData(records[0]);
          }
        }
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error("[useTodayAttendance]", err.code);
        setError(err.message);
        setLoading(false);
      },
    );

    return unsub;
  }, [userId]); // Only re-subscribe if userId changes

  return { data, loading, error };
}

/** Monthly attendance for a specific user. */
export function useMonthlyAttendance(userId, year, month) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setData([]);
      setLoading(false);
      return;
    }

    const mm = String(month).padStart(2, "0");
    const from = `${year}-${mm}-01`;
    const to = `${year}-${mm}-31`;

    setLoading(true);

    const q = query(
      collection(db, "attendance"),
      where("userId", "==", userId)
    );

    const unsub = onSnapshot(q, (snap) => {
      const records = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((d) => d.date >= from && d.date <= to);
      records.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
      setData(records);
      setLoading(false);
    }, (err) => {
      console.error("[useMonthlyAttendance]", err);
      setLoading(false);
    });

    return unsub;
  }, [userId, year, month]);

  return { data, loading };
}

/** Today's attendance for ALL employees (admin/lead view). */
export function useAllTodayAttendance() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const today = getTodayStr();

    const q = query(collection(db, "attendance"), where("date", "==", today));

    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    return unsub;
  }, []); // No deps — "today" is computed fresh inside the effect

  return { data, loading };
}

/**
 * useIsEmployeeOnLeave — real-time boolean for whether an employee has
 * approved leave covering today.
 */
export function useIsEmployeeOnLeave(userId) {
  const [data, setData] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setData(false);
      setLoading(false);
      return;
    }

    const today = getTodayStr();
    const q = query(
      collection(db, "leaveRequests"),
      where("userId", "==", userId),
      where("status", "==", "approved")
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const hasLeave = snap.docs.some((doc) => {
          const leave = doc.data();
          return leave.fromDate <= today && leave.toDate >= today;
        });
        setData(hasLeave);
        setLoading(false);
      },
      (err) => {
        console.error("[useIsEmployeeOnLeave]", err.code, err.message);
        setLoading(false);
      },
    );

    return unsub;
  }, [userId]);

  return { data, loading };
}

/**
 * useTodayHoliday — real-time holiday status for today's date.
 */
export function useTodayHoliday() {
  const [data, setData] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const today = getTodayStr();
    const q = query(collection(db, "holidays"), where("date", "==", today));

    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(!snap.empty);
        setLoading(false);
      },
      (err) => {
        console.error("[useTodayHoliday]", err.code, err.message);
        setLoading(false);
      },
    );

    return unsub;
  }, []);

  return { data, loading };
}

/**
 * useAttendanceByDate — real-time attendance records for ANY single date.
 * @param {string} dateStr  "YYYY-MM-DD" or "" to skip the query.
 */
export function useAttendanceByDate(dateStr) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!dateStr) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const q = query(
      collection(db, "attendance"),
      where("date", "==", dateStr)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        docs.sort((a, b) => {
          const t1 = a.checkIn?.toMillis?.() ?? 0;
          const t2 = b.checkIn?.toMillis?.() ?? 0;
          return t1 - t2;
        });
        setData(docs);
        setLoading(false);
      },
      (err) => {
        console.error("[useAttendanceByDate]", err.code);
        setLoading(false);
      },
    );

    return unsub;
  }, [dateStr]);

  return { data, loading };
}

/**
 * useAllMonthlyAttendance — real-time attendance for ALL employees for a full month.
 * @param {number} year   e.g. 2025
 * @param {number} month  1-12
 */
export function useAllMonthlyAttendance(year, month) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!year || !month) {
      setData([]);
      setLoading(false);
      return;
    }

    const mm = String(month).padStart(2, "0");
    const from = `${year}-${mm}-01`;
    const to = `${year}-${mm}-31`; // Firestore string compare handles month boundaries

    setLoading(true);

    const q = query(
      collection(db, "attendance"),
      where("date", ">=", from),
      where("date", "<=", to),
      orderBy("date", "asc"),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("[useAllMonthlyAttendance]", err.code);
        setLoading(false);
      },
    );

    return unsub;
  }, [year, month]);

  return { data, loading };
}

// ── Attendance write helpers ───────────────────────────────

// src/hooks/useFirestore.js
// ── Only the checkIn function changes — replace it with this ──────────────

export async function checkIn(
  userProfile,
  mode = "office",
  shiftStartTime = "09:00",
) {
  const userId = userProfile?.uid || userProfile?.id;
  if (!userId) {
    throw new Error("Validation Error: Missing user identifier.");
  }

  const today = getTodayStr();
  if (!today) {
    throw new Error("Validation Error: Invalid date.");
  }

  const now = Timestamp.now();


  // Prevent double check-in
  const existing = await getDocs(
    query(
      collection(db, "attendance"),
      where("userId", "==", userId),
      where("date", "==", today),
    ),
  );
  if (!existing.empty) {
    throw new Error("You have already checked in today.");
  }

  // Late detection — based on the employee's assigned shift start time
  // Falls back to "09:00" if no shift is assigned
  const checkInDate = now.toDate();
  const [shiftHour, shiftMin] = shiftStartTime.split(":").map(Number);
  const threshold = new Date(checkInDate);
  threshold.setHours(shiftHour, shiftMin, 0, 0);

  const isLate = checkInDate > threshold;
  const lateByMins = isLate ? Math.floor((checkInDate - threshold) / 60000) : 0;

  await addDoc(collection(db, "attendance"), {
    userId: userId,
    employeeId: userProfile.employeeId || "",
    displayName: userProfile.displayName || "",
    department: userProfile.department || "",
    teamId: userProfile.teamId || null,
    date: today,
    checkIn: now,
    checkOut: null,
    hoursWorked: 0,
    status: "present",
    mode,
    isLate,
    lateByMinutes: lateByMins,
    shiftStartTime, // stored for audit / admin display
    breaks: [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function checkOut(attendanceId, checkInTimestamp, shiftEndTime = "18:00") {
  const attDoc = await getDoc(doc(db, "attendance", attendanceId));
  if (!attDoc.exists()) throw new Error("Attendance record not found");
  
  const docData = attDoc.data() || {};
  let breaks = docData.breaks || [];
  
  const now = Timestamp.now();
  const checkOutDate = now.toDate();
  const checkInDate = checkInTimestamp.toDate();
  
  // 1. Automatically end active break if any
  const activeBreakIdx = breaks.findIndex((b) => !b.endTime);
  if (activeBreakIdx !== -1) {
    const activeBreak = breaks[activeBreakIdx];
    const msDuration = checkOutDate - activeBreak.startTime.toDate();
    const durationMins = Math.round(msDuration / 60000);
    
    const settingsDoc = await getDoc(doc(db, "orgSettings", "config"));
    const orgSettings = settingsDoc.exists() ? settingsDoc.data() : {};
    const breakPolicy = orgSettings.breakPolicy || {
      lunch: 60
    };
    const allowedMins = breakPolicy[activeBreak.breakType] || 15;
    const extraMins = durationMins > allowedMins ? durationMins - allowedMins : 0;
    
    const updatedBreaks = [...breaks];
    updatedBreaks[activeBreakIdx] = {
      ...activeBreak,
      endTime: now,
      duration: durationMins,
    };
    breaks = updatedBreaks;
    
    // Log extra break
    if (extraMins > 0) {
      try {
        await addDoc(collection(db, "extraBreaks"), {
          userId: docData.userId || "",
          employeeId: docData.employeeId || "",
          employeeName: docData.employeeName || "",
          teamId: docData.teamLeadId || null,
          department: docData.department || "",
          shiftName: docData.shiftName || "Unknown",
          breakType: activeBreak.breakType,
          startTime: activeBreak.startTime,
          endTime: now,
          allowedMins,
          actualMins: durationMins,
          extraMins,
          date: docData.date,
          status: "Late from Break",
          createdAt: serverTimestamp(),
        });
      } catch (err) {
        console.error("[checkOut] Error saving extra break log:", err);
      }
    }
  }

  // 2. Fetch break policies for calculating excess break impact
  const settingsDoc = await getDoc(doc(db, "orgSettings", "config"));
  const orgSettings = settingsDoc.exists() ? settingsDoc.data() : {};
  const breakPolicy = orgSettings.breakPolicy || {
    lunch: 60
  };

  let totalBreakMins = 0;
  let totalExcessBreakMins = 0;
  breaks.forEach((b) => {
    if (b.endTime) {
      totalBreakMins += b.duration || 0;
      const allowedMins = breakPolicy[b.breakType] || 15;
      totalExcessBreakMins += Math.max(0, b.duration - allowedMins);
    }
  });

  // 3. Calculate working hours
  const msWorked = checkOutDate - checkInDate;
  const grossHours = Math.round((msWorked / 3_600_000) * 100) / 100;
  const totalBreakHours = totalBreakMins / 60;
  const excessBreakHours = totalExcessBreakMins / 60;
  
  // Net hours worked: gross minus total break hours (standard productive hours)
  let productiveHours = Math.round((grossHours - totalBreakHours) * 100) / 100;
  if (productiveHours < 0) productiveHours = 0;

  // 4. Calculate early checkout / overtime against scheduled shift end
  const [eh, em] = (shiftEndTime || "18:00").split(":").map(Number);
  const shiftEnd = new Date(checkInDate);
  shiftEnd.setHours(eh, em, 0, 0);
  
  const shiftStartTime = docData.shiftStartTime || "09:00";
  const [sh, sm] = shiftStartTime.split(":").map(Number);
  if (eh < sh || (eh === sh && em < sm)) {
    shiftEnd.setDate(shiftEnd.getDate() + 1);
  }

  const isEarly = checkOutDate < shiftEnd;
  const earlyCheckOut = isEarly;
  
  let overtimeHours = 0;
  if (checkOutDate > shiftEnd) {
    const msOvertime = checkOutDate - shiftEnd;
    overtimeHours = Math.round((msOvertime / 3_600_000) * 100) / 100;
  }

  // Attendance/checkout status policy
  let checkoutStatus = "Completed";
  let isHalfDay = false;

  if (isEarly) {
    const earlyByMs = shiftEnd - checkOutDate;
    const earlyByHours = earlyByMs / 3_600_000;
    if (earlyByHours >= 4 || productiveHours < 4) {
      checkoutStatus = "Half Day";
      isHalfDay = true;
    } else {
      checkoutStatus = "Early Punch Out";
    }
  } else if (overtimeHours > 0) {
    checkoutStatus = "Overtime";
  }

  await updateDoc(doc(db, "attendance", attendanceId), {
    checkOut: now,
    hoursWorked: productiveHours, // Keep hoursWorked as net productive time
    grossHours,
    totalBreakMins,
    productiveHours,
    overtimeHours,
    earlyCheckOut,
    checkoutStatus,
    breaks,
    status: "checked-out",
    updatedAt: serverTimestamp(),
  });
}

/**
 * startBreak — Start a break session (tea1, tea2, or lunch).
 * @param {string} attendanceId  - Attendance document ID
 * @param {string} breakType     - "tea1" | "tea2" | "lunch"
 */
export async function startBreak(attendanceId, breakType) {

  const now = Timestamp.now();
  const breakRecord = {
    breakType,
    startTime: now,
    endTime: null,
    duration: 0,
  };

  await updateDoc(doc(db, "attendance", attendanceId), {
    breaks: arrayUnion(breakRecord),
    status: "on-break",
    updatedAt: serverTimestamp(),
  });
}

/**
 * endBreak — End the current break.
 * @param {string} attendanceId  - Attendance document ID
 */
export async function endBreak(attendanceId) {

  const attDoc = await getDoc(doc(db, "attendance", attendanceId));
  if (!attDoc.exists()) throw new Error("Attendance record not found");

  const docData = attDoc.data();
  const breaks = docData.breaks || [];
  const activeBreakIdx = breaks.findIndex((b) => !b.endTime);

  if (activeBreakIdx === -1) throw new Error("No active break found");

  const activeBreak = breaks[activeBreakIdx];
  const now = Timestamp.now();
  const msDuration = now.toDate() - activeBreak.startTime.toDate();
  const durationMins = Math.round(msDuration / 60000);

  const settingsDoc = await getDoc(doc(db, "orgSettings", "config"));
  const orgSettings = settingsDoc.exists() ? settingsDoc.data() : {};
  const breakPolicy = orgSettings.breakPolicy || {
    lunch: 60
  };
  
  const allowedMins = breakPolicy[activeBreak.breakType] || 15;
  const extraMins = durationMins > allowedMins ? durationMins - allowedMins : 0;

  // Update the active break with end time
  const updatedBreaks = [...breaks];
  updatedBreaks[activeBreakIdx] = {
    ...activeBreak,
    endTime: now,
    duration: durationMins,
  };

  await updateDoc(doc(db, "attendance", attendanceId), {
    breaks: updatedBreaks,
    status: "present",
    updatedAt: serverTimestamp(),
  });

  // Log Extra Break
  if (extraMins > 0) {
    try {
      await addDoc(collection(db, "extraBreaks"), {
        userId: docData.userId || "",
        employeeId: docData.employeeId || "",
        employeeName: docData.employeeName || "",
        teamId: docData.teamLeadId || null,
        department: docData.department || "",
        shiftName: docData.shiftName || "Unknown",
        breakType: activeBreak.breakType,
        startTime: activeBreak.startTime,
        endTime: now,
        allowedMins,
        actualMins: durationMins,
        extraMins,
        date: docData.date,
        status: "Late from Break",
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("[endBreak] Error saving extra break log:", err);
    }
  }
}

/**
 * isEmployeeOnLeave — Check if employee has approved leave today.
 * @param {string} userId  - Employee UID
 * @param {string} todayStr - "YYYY-MM-DD"
 * @returns {boolean}
 */
export async function isEmployeeOnLeave(userId, todayStr) {
  const leaves = await getDocs(
    query(
      collection(db, "leaveRequests"),
      where("userId", "==", userId),
      where("status", "==", "approved"),
    ),
  );

  return leaves.docs.some(
    (l) => l.data().fromDate <= todayStr && l.data().toDate >= todayStr,
  );
}

/**
 * isTodayHoliday — Check if today is a declared holiday.
 * @param {string} todayStr  - "YYYY-MM-DD"
 * @returns {boolean}
 */
export async function isTodayHoliday(todayStr) {
  const holidays = await getDocs(
    query(collection(db, "holidays"), where("date", "==", todayStr)),
  );

  return !holidays.empty;
}

// ────────────────────────────────────────────────────────────
// TASK HOOKS
// ────────────────────────────────────────────────────────────

export function normalizeStatus(status) {
  if (!status) return "PENDING";
  const s = String(status).toUpperCase().trim();
  if (s === "PENDING" || s === "TODO" || s === "TO DO") return "PENDING";
  if (s === "IN_PROGRESS" || s === "IN PROGRESS" || s === "PROGRESS") return "IN_PROGRESS";
  if (s === "COMPLETED" || s === "DONE" || s === "COMPLETE") return "COMPLETED";
  if (s === "BLOCKED") return "BLOCKED";
  return "PENDING"; // Fallback
}

/** Tasks assigned TO a specific employee. */
export function useMyTasks(userId) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setData([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "tasks"),
      where("assignedTo", "==", userId),
      orderBy("createdAt", "desc"),
    );

    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => {
        const raw = d.data();
        return { id: d.id, ...raw, status: normalizeStatus(raw.status) };
      }));
      setLoading(false);
    });

    return unsub;
  }, [userId]);

  return { data, loading };
}

/** All tasks where teamId matches a lead's UID. */
export function useTeamTasks(leadId) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leadId) {
      setData([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "tasks"),
      where("teamId", "==", leadId),
      orderBy("createdAt", "desc"),
    );

    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => {
        const raw = d.data();
        return { id: d.id, ...raw, status: normalizeStatus(raw.status) };
      }));
      setLoading(false);
    });

    return unsub;
  }, [leadId]);

  return { data, loading };
}

/** All tasks in the org (admin view). */
export function useAllTasks() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "tasks"), orderBy("createdAt", "desc"));

    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => {
        const raw = d.data();
        return { id: d.id, ...raw, status: normalizeStatus(raw.status) };
      }));
      setLoading(false);
    });

    return unsub;
  }, []);

  return { data, loading };
}

// ── Task write helpers ─────────────────────────────────────

async function createNotification({
  title,
  message,
  recipientId,
  recipientType = "user",
  type = "general",
  priority = "normal",
  actionUrl = "",
  createdBy = "",
  metadata = {},
}) {
  await addDoc(collection(db, "notifications"), {
    title,
    message,
    recipientId,
    recipientType,
    type,
    priority,
    actionUrl,
    createdBy,
    metadata,
    readBy: [],
    createdAt: serverTimestamp(),
  });
}


export async function createTask({
  title,
  description,
  assignedTo,
  assignedToName,
  assignedBy,
  assignedByName,
  teamId,
  department,
  priority,
  deadline,
}) {
  // ── Title validation ──────────────────────────────────────
  if (!title || !title.trim()) {
    throw new Error("Task title is required");
  }
  if (title.length > 100) {
    throw new Error("Task title cannot exceed 100 characters");
  }

  // ── Duplicate check ───────────────────────────────────────
  const dupQuery = query(
    collection(db, "tasks"),
    where("assignedTo", "==", assignedTo)
  );
  const dupSnap = await getDocs(dupQuery);
  const isDuplicate = dupSnap.docs.some((docSnap) => {
    const task = docSnap.data();
    return task.title.trim().toLowerCase() === title.trim().toLowerCase();
  });
  if (isDuplicate) {
    throw new Error("This task has already been assigned to the selected user.");
  }

  // ── Auto-increment taskId (T_ID01, T_ID02, …) ─────────────
  const coll = collection(db, "tasks");
  const idSnap = await getDocs(query(coll, orderBy("createdAt", "desc"), limit(1)));
  let newIdNum = 1;
  if (!idSnap.empty) {
    const lastTask = idSnap.docs[0].data();
    if (lastTask.taskId && lastTask.taskId.startsWith("T_ID")) {
      const num = parseInt(lastTask.taskId.replace("T_ID", ""), 10);
      if (!isNaN(num)) newIdNum = num + 1;
    }
  }
  const taskIdStr = "T_ID" + String(newIdNum).padStart(2, "0");

  const taskRef = await addDoc(coll, {
    taskId: taskIdStr,
    title,
    description: description || "",
    assignedTo,
    assignedToName,
    assignedBy,
    assignedByName,
    teamId,
    department,
    priority: priority || "medium",
    status: "PENDING",
    isRead: false,
    deadline: Timestamp.fromDate(new Date(deadline)),
    completedAt: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
});
await createNotification({
    title: "New Task Assigned",
    message: `You have been assigned a new task: "${title}"`,
    recipientId: assignedTo,
    recipientType: "user",
    type: "task",
    priority,
    actionUrl: "/employee/tasks",
    createdBy: assignedBy,
    metadata: {
        taskId: taskRef.id,
        taskNumber: taskIdStr,
    },
});

}

export async function updateTaskStatus(taskId, status) {
  const normalized = normalizeStatus(status);
  const updates = { status: normalized, updatedAt: serverTimestamp() };
  if (normalized === "COMPLETED") updates.completedAt = serverTimestamp();
  await updateDoc(doc(db, "tasks", taskId), updates);
}

export async function updateTask(taskId, updates, userProfile) {
  const taskDocRef = doc(db, "tasks", taskId);
  const taskSnap = await getDoc(taskDocRef);
  if (!taskSnap.exists()) {
    throw new Error("Task not found");
  }
  const taskData = taskSnap.data();

  // Authorization check
  const isAdmin = userProfile?.role === "admin";
  const isAssigner = taskData.assignedBy === userProfile?.uid;
  const isLead = taskData.teamId === userProfile?.uid;
  if (!isAdmin && !isAssigner && !isLead) {
    throw new Error("Unauthorized to edit this task");
  }

  // Title Validation
  if (updates.title !== undefined) {
    if (!updates.title || !updates.title.trim()) {
      throw new Error("Task title is required");
    }
    if (updates.title.length > 100) {
      throw new Error("Task title cannot exceed 100 characters");
    }
  }

  // Duplicate check on update
  const targetAssignedTo = updates.assignedTo !== undefined ? updates.assignedTo : taskData.assignedTo;
  const targetTitle = updates.title !== undefined ? updates.title : taskData.title;

  if (updates.assignedTo !== undefined || updates.title !== undefined) {
    const q = query(
      collection(db, "tasks"),
      where("assignedTo", "==", targetAssignedTo)
    );
    const snap = await getDocs(q);
    const isDuplicate = snap.docs.some(docSnap => {
      if (docSnap.id === taskId) return false; // exclude current task
      const task = docSnap.data();
      return task.title.trim().toLowerCase() === targetTitle.trim().toLowerCase();
    });

    if (isDuplicate) {
      throw new Error("This task has already been assigned to the selected user.");
    }
  }

  const taskUpdates = { ...updates };
  if (taskUpdates.deadline) {
    taskUpdates.deadline = Timestamp.fromDate(new Date(taskUpdates.deadline));
  }
  if (taskUpdates.status) {
    taskUpdates.status = normalizeStatus(taskUpdates.status);
    if (taskUpdates.status === "COMPLETED") {
      taskUpdates.completedAt = serverTimestamp();
    }
  }

  await updateDoc(taskDocRef, {
    ...taskUpdates,
    updatedAt: serverTimestamp(),
  });
}

export async function deleteTask(taskId, userProfile) {
  const taskDocRef = doc(db, "tasks", taskId);
  const taskSnap = await getDoc(taskDocRef);
  if (!taskSnap.exists()) {
    throw new Error("Task not found");
  }
  const taskData = taskSnap.data();

  // Authorization check
  const isAdmin = userProfile?.role === "admin";
  const isAssigner = taskData.assignedBy === userProfile?.uid;
  const isLead = taskData.teamId === userProfile?.uid;
  if (!isAdmin && !isAssigner && !isLead) {
    throw new Error("Unauthorized to delete this task");
  }

  await deleteDoc(taskDocRef);
}


// ────────────────────────────────────────────────────────────
// MEETING HOOKS
// ────────────────────────────────────────────────────────────

/**
 * useTodayMeetings — realtime listener for TODAY'S meetings ONLY.
 *
 * Key design decisions:
 *  • Queries date == today (string "YYYY-MM-DD") — no future/past leakage.
 *  • Meetings are stored with field `time` (HH:mm) by AdminMeetings.
 *  • `endTime` is computed from `time + duration` if not stored.
 *  • Client-side tick every 60 s re-filters expired meetings from
 *    the list so the dashboard updates automatically when time passes.
 *
 * Returns:
 *   activeMeetings  — today's meetings where end time has NOT passed
 *   allTodayMeetings — every meeting today (for meeting pages)
 *   loading
 */
export function useTodayMeetings() {
  const [raw, setRaw] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  // Subscribe to today's meetings in Firestore (date == today, exact match)
  // Sorted client-side to prevent composite index failed-precondition errors
  useEffect(() => {
    const today = getTodayStr();
    const q = query(
      collection(db, "meetings"),
      where("date", "==", today)
    );
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => (a.time || "").localeCompare(b.time || ""));
      setRaw(docs);
      setLoading(false);
    }, (err) => {
      console.error("[useTodayMeetings]", err);
      setLoading(false);
    });
    return unsub;
  }, []);

  // Tick every 30 seconds so expired meetings are removed without refresh
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  // Derive active meetings (end time not yet passed)
  const now = new Date();
  const allTodayMeetings = raw.map((m) => {
    // Normalise end time: prefer stored endTime, otherwise compute from time+duration
    const endTimeStr =
      m.endTime ||
      (m.duration && m.time
        ? calculateEndTime(m.time, m.duration)
        : m.time);
    return { ...m, _endTimeStr: endTimeStr };
  });

  const activeMeetings = allTodayMeetings
    .filter((m) => {
      if (!m.date || !m._endTimeStr) return true; // Guard: show if missing fields
      return new Date(`${m.date}T${m._endTimeStr}`) > now;
    })
    .sort((a, b) => (a.time || "").localeCompare(b.time || ""));

  // Keep backward compat alias so existing code using useUpcomingMeetings still works
  return {
    data: activeMeetings,       // active (not-yet-expired) today meetings
    allTodayMeetings,           // all today meetings incl. expired
    loading,
    _tick: tick,                // consumed internally
  };
}

/**
 * useUpcomingMeetings — backward-compatible alias for useTodayMeetings.
 * Existing pages (AdminDashboard, EmployeeDashboard) call this.
 * Now returns ONLY today's active meetings instead of all future ones.
 */
export function useUpcomingMeetings() {
  return useTodayMeetings();
}

/**
 * useAllMeetings — all meetings (for admin/lead scheduling pages).
 * Fetches full history without a date filter.
 * Simplified query without composite index: only orderBy("date", "desc")
 */
export function useAllMeetings() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, "meetings"),
      orderBy("date", "desc"),
      limit(200),
    );
    const unsub = onSnapshot(q, (snap) => {
      const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      docs.sort((a, b) => {
        const d1 = b.date || "";
        const d2 = a.date || "";
        const c = d1.localeCompare(d2);
        if (c !== 0) return c;
        return (b.time || "").localeCompare(a.time || "");
      });
      setData(docs);
      setLoading(false);
    }, (err) => {
      console.error("[useAllMeetings]", err);
      setLoading(false);
    });
    return unsub;
  }, []);

  return { data, loading };
}

/**
 * calculateEndTime — adds duration (mins) to HH:mm start time.
 * returns "HH:mm"
 */
export function calculateEndTime(startTime, durationMins) {
  if (!startTime) return "";
  if (!durationMins) return startTime;
  const [h, m] = startTime.split(":").map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  d.setMinutes(d.getMinutes() + parseInt(durationMins, 10));
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

export async function scheduleMeeting({
  title,
  agenda,
  scheduledBy,
  scheduledByName,
  date,
  time,
  duration = 60,
  meetLink,
  audience,
}) {
  const endTime = calculateEndTime(time, duration);
  await addDoc(collection(db, "meetings"), {
    title,
    agenda: agenda || "",
    scheduledBy,
    scheduledByName,
    date,
    time,
    duration: parseInt(duration, 10),
    endTime,
    meetLink: meetLink || "",
    audience: audience || "all",
    status: "upcoming",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

// ────────────────────────────────────────────────────────────
// ANNOUNCEMENT HOOKS
// ────────────────────────────────────────────────────────────

/**
 * Real-time announcements.
 * @param {string|string[]} audience - "all" (admin) or an array like
 *   ["employees", dept, teamId] / ["leads", dept, teamId, uid].
 * @param {string} userRole - the viewer's role: "admin" | "lead" | "employee".
 *   Pass "admin" (default) to see all announcements without restriction.
 */
export function useAnnouncements(audience = "all", userRole = "admin") {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const audStr = Array.isArray(audience) ? [...audience].sort().join(",") : audience;

  useEffect(() => {
    setLoading(true);

    const q = query(
      collection(db, "announcements"),
      orderBy("createdAt", "desc")
    );

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const rows = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
          attachments: Array.isArray(docSnap.data().attachments)
            ? docSnap.data().attachments
            : [],
        }));

        // ── Audience filtering ──────────────────────────────────
        let filtered = rows;

        if (userRole !== "admin") {
          const audArray = Array.isArray(audience) ? audience : [audience];

          filtered = rows.filter((a) => {
            // "all" announcements are visible to every non-admin
            if (a.audience === "all") return true;

            // "leads" audience → strictly only Team Lead users
            if (a.audience === "leads") {
              return userRole === "lead";
            }

            // "employees" audience → strictly only Employee users
            if (a.audience === "employees") {
              return userRole === "employee";
            }

            // department / teamId / uid specific audiences
            return audArray.includes(a.audience);
          });
        }

        setData(filtered);
        setLoading(false);
      },
      (err) => {
        console.error("[useAnnouncements]", err);
        setLoading(false);
      }
    );

    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audStr, userRole]);

  return {
    data,
    loading,
  };
}

// PATCH for src/hooks/useFirestore.js
// Replace the existing postAnnouncement function with this version
// so it returns the new document reference (needed for attachment upload).

// Also add markAnnouncementRead below it.

export async function postAnnouncement({
  title,
  message,
  priority,
  audience,
  postedBy,
  postedByName,
  teamId,
  teamName,
  attachments = [],
}) {
  if (!title || !title.trim()) throw new Error("Title cannot be empty");
  if (!message || !message.trim()) throw new Error("Message cannot be empty");

  // Prevent duplicate announcements (same title, message, and teamId)
  const q = query(
    collection(db, "announcements"),
    where("title", "==", title.trim()),
    where("message", "==", message.trim()),
    where("teamId", "==", teamId || null)
  );
  const snap = await getDocs(q);
  if (!snap.empty) {
    throw new Error("Duplicate announcement detected.");
  }

  const docRef = await addDoc(collection(db, "announcements"), {
    title: title.trim(),
    message: message.trim(),
    priority: priority || "medium",
    audience: audience || "all",
    postedBy,
    postedByName,
    teamId: teamId || null,
    teamName: teamName || null,
    createdBy: postedBy,
    readBy: [],
    attachments,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await updateDoc(docRef, { announcementId: docRef.id });

  return {
    id: docRef.id,
    announcementId: docRef.id,
    title: title.trim(),
    message: message.trim(),
    priority: priority || "medium",
    audience: audience || "all",
    postedBy,
    postedByName,
    teamId: teamId || null,
    teamName: teamName || null,
    createdBy: postedBy,
    readBy: [],
    attachments,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

export function useTeamLeadAnnouncements(leadId) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leadId) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const q = query(
  collection(db, "announcements"),
  orderBy("createdAt", "desc")
);
    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const rows = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
          attachments: Array.isArray(docSnap.data().attachments)
            ? docSnap.data().attachments
            : [],
        }));

        setData(rows);
        setLoading(false);
      },
      (err) => {
        console.error("[useTeamLeadAnnouncements]", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [leadId]);

  return { data, loading };
}

export function useEmployeeAnnouncements(teamId) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!teamId) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);

   const q = query(
  collection(db, "announcements"),
  orderBy("createdAt", "desc")
);

    const unsub = onSnapshot(
      q,
      (snapshot) => {
        const rows = snapshot.docs
  .map((docSnap) => ({
    id: docSnap.id,
    ...docSnap.data(),
    attachments: Array.isArray(docSnap.data().attachments)
      ? docSnap.data().attachments
      : [],
  }))
  .filter((a) => {
    return (
      a.audience === "all" ||
      a.teamId === teamId
    );
  });

setData(rows);
        setLoading(false);
      },
      (err) => {
        console.error("[useEmployeeAnnouncements]", err);
        setLoading(false);
      }
    );

    return () => unsub();
  }, [teamId]);

  return { data, loading };
}

/**
 * markAnnouncementRead — add userId to readBy array.
 * Safe to call multiple times (arrayUnion is idempotent).
 */
export async function markAnnouncementRead(announcementId, userId) {
  await updateDoc(doc(db, "announcements", announcementId), {
    readBy: arrayUnion(userId),
    updatedAt: serverTimestamp(),
  });
}
// ────────────────────────────────────────────────────────────
// USER HOOKS
// ────────────────────────────────────────────────────────────

/** All users ordered by name — used by admin. */
export function useAllUsers() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "users"), orderBy("displayName", "asc"));

    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    return unsub;
  }, []);

  return { data, loading };
}

/** Team members under a specific lead. */
export function useTeamMembers(leadId) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leadId) {
      setData([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "users"),
      where("teamId", "==", leadId),
      orderBy("displayName", "asc"),
    );

    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    return unsub;
  }, [leadId]);

  return { data, loading };
}

// ────────────────────────────────────────────────────────────
// LEAVE REQUEST HOOKS
// ────────────────────────────────────────────────────────────

export function useMyLeaveRequests(userId) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) {
      setData([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "leaveRequests"),
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
    );

    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    return unsub;
  }, [userId]);

  return { data, loading };
}

export function useTeamLeaveRequests(leadId) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leadId) {
      setData([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "leaveRequests"),
      where("teamLeadId", "==", leadId),
      where("status", "==", "pending_lead"),
      orderBy("createdAt", "desc"),
    );

    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    return unsub;
  }, [leadId]);

  return { data, loading };
}

/** All leave requests for a team (not just pending) */
export function useTeamLeaves(leadId) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!leadId) {
      setData([]);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, "leaveRequests"),
      where("teamLeadId", "==", leadId),
      orderBy("createdAt", "desc"),
    );

    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    return unsub;
  }, [leadId]);

  return { data, loading };
}

/**
 * Centralized logic to determine the initial status of a leave request
 */
export function getInitialLeaveStatus(user) {
  const role = (user?.role || "").toLowerCase();
  const leadId = user?.teamLeadId || user?.teamId || "";

  if (role === "admin") {
    return "approved";
  }

  if (role === "lead" || role === "team_lead") {
    return "pending_admin";
  }

  if (!leadId || leadId === "" || leadId === "null" || leadId === "undefined") {
    return "pending_admin";
  }

  return "pending_lead";
}

export function useAllLeaveRequests() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(
      collection(db, "leaveRequests"),
      orderBy("createdAt", "desc"),
    );

    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });

    return unsub;
  }, []);

  return { data, loading };
}

export async function submitLeaveRequest(
  userProfile,
  {
    leaveType,
    fromDate,
    toDate,
    reason,
    handoverTo,
    handoverToName,
    totalDays: passedDays,
    medicalCertificateName,
    medicalCertificateData,
    isHalfDay,
    halfDaySession,
    deductFrom,
  },
) {
  let totalDays = passedDays;
  if (!totalDays || typeof totalDays !== "number") {
    let days = 0;
    let curr = new Date(fromDate);
    const to = new Date(toDate);
    while (curr <= to) {
      const day = curr.getDay();
      if (day !== 0 && day !== 6) days++;
      curr.setDate(curr.getDate() + 1);
    }
    totalDays = days;
  }

  // 🔹 CENTRALIZED ROLE-BASED LEAVE ROUTING
  const status = getInitialLeaveStatus(userProfile);

  const teamLeadId = userProfile.teamLeadId || userProfile.teamId || null;
  const leaveRef = await addDoc(collection(db, "leaveRequests"), {
    userId: userProfile.uid,
    employeeId: userProfile.employeeId || "",
    employeeName: userProfile.displayName || "",
    displayName: userProfile.displayName || "",
    role: userProfile.role || "employee",
    department: userProfile.department || "",
    teamId: teamLeadId,
    teamLeadId: teamLeadId,
    leaveType,
    fromDate,
    toDate,
    totalDays,
    reason,
    handoverTo: handoverTo || null,
    handoverToName: handoverToName || "",
    medicalCertificateName: medicalCertificateName || null,
    medicalCertificateData: medicalCertificateData || null,
    isHalfDay: !!isHalfDay,
    halfDaySession: halfDaySession || null,
    deductFrom: deductFrom || leaveType,
    status,
    reviewedBy: null,
    reviewNote: "",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

await createNotification({
  title: "New Leave Request",
  message: `${userProfile.displayName} has submitted a leave request.`,
  recipientId:
    status === "pending_lead"
      ? teamLeadId
      : "admins",   // or the appropriate admin recipient used in your app
  recipientType: "user",
  type: "leave",
  priority: "normal",
  actionUrl:
    status === "pending_lead"
      ? "/lead/leave"
      : "/admin/leave",
  createdBy: userProfile.uid,
  metadata: {
    leaveId: leaveRef.id,
    leaveType,
    fromDate,
    toDate,
  },
});

  // If auto-approved (Admin), deduct balance immediately
  if (status === "approved") {
    await deductLeaveBalance(userProfile.uid, leaveType, totalDays);
  }
}

export async function reviewLeaveRequest(
  requestId,
  status,
  reviewedBy,
  reviewNote = "",
) {
  // Update leave request
  await updateDoc(doc(db, "leaveRequests", requestId), {
    status,
    reviewedBy,
    reviewNote,
    updatedAt: serverTimestamp(),
  });

  // Get updated leave request
  const leaveSnap = await getDoc(doc(db, "leaveRequests", requestId));

  if (!leaveSnap.exists()) return;

  const leave = leaveSnap.data();

  // Send notification to employee
  await createNotification({
    title: status === "approved" ? "Leave Approved" : "Leave Rejected",
    message:
      status === "approved"
        ? "Your leave request has been approved."
        : "Your leave request has been rejected.",
    recipientId: leave.userId,
    recipientType: "user",
    type: "leave",
    priority: "normal",
    actionUrl: "/employee/leave",
    createdBy: reviewedBy,
    metadata: {
      leaveId: requestId,
    },
  });
}




export async function deductLeaveBalance(userId, leaveType, days) {
  await updateDoc(doc(db, "users", userId), {
    [`leaveBalance.${leaveType}`]: increment(-days),
    updatedAt: serverTimestamp(),
  });
}

// ────────────────────────────────────────────────────────────
// ORG SETTINGS
// ────────────────────────────────────────────────────────────

export function useOrgSettings() {
  return useDocument("orgSettings", "config");
}
// ────────────────────────────────────────────────────────────
// MEETING STATUS HELPERS
// ────────────────────────────────────────────────────────────

export async function joinMeeting(userId, meetingId) {
  await updateDoc(doc(db, "users", userId), {
    inMeeting: true,
    currentMeetingId: meetingId,
    updatedAt: serverTimestamp(),
  });
}

export async function leaveMeeting(userId) {
  await updateDoc(doc(db, "users", userId), {
    inMeeting: false,
    currentMeetingId: null,
    updatedAt: serverTimestamp(),
  });
}
// ────────────────────────────────────────────────────────────
// UTILITY FUNCTIONS (pure — no hooks)
// ────────────────────────────────────────────────────────────

/** Returns today's date as "YYYY-MM-DD" in local time. */
export function getTodayStr() {
  const d = new Date();
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
}

/** Format a Firestore Timestamp (or Date) to "09:30 AM". */
export function formatTime(timestamp) {
  if (!timestamp) return "—";
  const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return d.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

/** Format a Firestore Timestamp to "15 Apr 2025". */
export function formatDate(timestamp) {
  if (!timestamp) return "—";
  const d = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return d.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// ────────────────────────────────────────────────────────────
// TEAM-SCOPED HOOKS (Lead panel — avoids full-org fetches)
// ────────────────────────────────────────────────────────────

/**
 * useTeamTodayAttendance — today's attendance ONLY for a given
 * set of memberIds.  Replaces useAllTodayAttendance in LeadContext
 * so we stop fetching the entire org's attendance on every lead login.
 *
 * memberIds: string[]  — uid list from useTeamMembers
 * Firestore `in` supports up to 30 values; teams are normally < 30.
 */
export function useTeamTodayAttendance(memberIds) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Stable key so the effect only re-runs when the actual ids change
  const idsKey = [...memberIds].sort().join(",");

  useEffect(() => {
    if (!memberIds || memberIds.length === 0) {
      setData([]);
      setLoading(false);
      return;
    }

    const today = getTodayStr();

    // Firestore `in` clause — chunk to 30 just in case
    const chunks = [];
    for (let i = 0; i < memberIds.length; i += 30) {
      chunks.push(memberIds.slice(i, i + 30));
    }

    const allDocs = {};
    let settled = 0;
    const unsubs = chunks.map((chunk) => {
      const q = query(
        collection(db, "attendance"),
        where("userId", "in", chunk),
        where("date", "==", today),
      );
      return onSnapshot(q, (snap) => {
        snap.docs.forEach((d) => { allDocs[d.id] = { id: d.id, ...d.data() }; });
        settled++;
        if (settled >= chunks.length) {
          setData(Object.values(allDocs));
          setLoading(false);
        }
      });
    });

    return () => unsubs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  return { data, loading };
}

/**
 * useTeamMonthlyAttendance — monthly attendance scoped to a team.
 * Used in LeadAnalytics instead of useAllMonthlyAttendance.
 */
export function useTeamMonthlyAttendance(memberIds, year, month) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  const idsKey = [...memberIds].sort().join(",");

  useEffect(() => {
    if (!memberIds || memberIds.length === 0 || !year || !month) {
      setData([]);
      setLoading(false);
      return;
    }

    const mm = String(month).padStart(2, "0");
    const from = `${year}-${mm}-01`;
    const to = `${year}-${mm}-31`;

    const chunks = [];
    for (let i = 0; i < memberIds.length; i += 30) {
      chunks.push(memberIds.slice(i, i + 30));
    }

    const allDocs = {};
    let settled = 0;
    const unsubs = chunks.map((chunk) => {
      const q = query(
        collection(db, "attendance"),
        where("userId", "in", chunk),
        where("date", ">=", from),
        where("date", "<=", to)
      );
      return onSnapshot(q, (snap) => {
        snap.docs.forEach((d) => { allDocs[d.id] = { id: d.id, ...d.data() }; });
        settled++;
        if (settled >= chunks.length) {
          const docsList = Object.values(allDocs);
          docsList.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
          setData(docsList);
          setLoading(false);
        }
      });
    });

    return () => unsubs.forEach((u) => u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, year, month]);

  return { data, loading };
}


export function useDepartments() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "departments"), orderBy("name", "asc"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setDepartments(list);
        setLoading(false);
      },
      (err) => {
        console.error("[useDepartments] error:", err);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  return { departments, loading };
}

/** Adds a new department to Firestore. */
export async function addDepartment(name) {
  if (!name || !name.trim()) throw new Error("Department name is required.");
  await addDoc(collection(db, "departments"), {
    name: name.trim(),
    active: true,
    createdAt: serverTimestamp(),
  });
}

/** Toggles active status of a department. */
export async function toggleDepartmentActive(deptId, active) {
  if (!deptId) throw new Error("Department ID is required.");
  const ref = doc(db, "departments", deptId);
  await updateDoc(ref, {
    active: !!active,
    updatedAt: serverTimestamp(),
  });
}

/** Deletes a department from Firestore. */
export async function deleteDepartment(deptId) {
  if (!deptId) throw new Error("Department ID is required.");
  const ref = doc(db, "departments", deptId);
  await deleteDoc(ref);
}